import React, { ReactNode } from 'react'
import styled from '@emotion/styled'
import { selectContrastingColor } from '@classy/campaign-page-blocks'
import type * as CSS from 'csstype'
import { DEFAULT_THEME_COLORS, DEFAULT_FOCUS_STYLES, getBodyFonts } from 'css/theme'
import useConfig from 'modules/config/state/use-config'
import { getBorderRadius } from 'utils/getBorderRadius'

export interface ActionButtonProps {
  /** A function to be called when the button is clicked. */
  onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void
  /** Optional button color. Defaults to Theme Neutral 20 */
  color?: CSS.Property.Color
  /** Children for setting the button text */
  children: ReactNode
  /** Acessible text for screen readers */
  ariaLabel?: string
  /** Boolean to set disabled on button */
  disabled?: boolean
  /** Button type for setting things like type="submit" */
  type?: 'button' | 'submit' | 'reset' | undefined
  size?: 'sm' | 'md' | 'lg' | 'xl'
  borderColor?: string
  borderRadius?: string
  dataId?: string
}

type StyledActionButtonProps = {
  color: CSS.Property.Color
  disabled: boolean
  height: string
  borderColor?: string
  borderRadius?: string
}

const StyledActionButton = styled.button(
  ({ color, disabled, height, borderColor, borderRadius }: StyledActionButtonProps) => ({
    backgroundColor: color,
    color: selectContrastingColor(color),
    width: '100%',
    height: height,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '16px',
    border: borderColor ? `1px solid ${borderColor}` : 'none',
    borderRadius: borderRadius,
    gap: '4px',
    transition: 'all 0.25s ease-in-out',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? '0.65' : '1',
    textAlign: 'center',
    textUnderlinePosition: 'from-font',
    textDecorationSkipInk: 'none',
    ...getBodyFonts('lg', true),
    '&:hover': {
      transform: disabled ? 'none' : 'translateY(-1px) scale(1.02)',
      filter: disabled ? 'none' : 'brightness(.96)',
    },
    '&:active': {
      transform: disabled ? 'none' : 'translateY(0px) scale(0.98)',
      filter: disabled ? 'none' : 'brightness(1)',
    },
    '&:focus': {
      outline: DEFAULT_FOCUS_STYLES['--theme--focus-outline'],
      outlineOffset: DEFAULT_FOCUS_STYLES['--theme--focus-outline-offset'],
    },
  }),
)

export const ActionButton = React.forwardRef<HTMLButtonElement, ActionButtonProps>(
  (
    {
      onClick,
      color = DEFAULT_THEME_COLORS['--theme-color--neutral-20'],
      ariaLabel,
      children,
      disabled = false,
      type,
      size,
      borderColor,
      dataId,
    },
    ref,
  ) => {
    const config = useConfig()
    const borderRadius = getBorderRadius(config.donationForm?.borderRadius)

    let height

    switch (size) {
      case 'sm':
        height = '32px'
        break
      case 'md':
        height = '40px'
        break
      case 'lg':
        height = '56px'
        break
      case 'xl':
        height = '64px'
        break
      default:
        height = '100%'
    }

    return (
      <StyledActionButton
        onClick={onClick}
        color={color}
        aria-label={ariaLabel}
        disabled={disabled}
        type={type}
        height={height}
        borderColor={borderColor}
        borderRadius={borderRadius}
        data-id={dataId || ''}
        ref={ref}
      >
        {children}
      </StyledActionButton>
    )
  },
)

ActionButton.displayName = 'ActionButton'
